import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { App, DatePicker, Form, Input, InputNumber, Select, Tooltip, Typography } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type { z } from 'zod'
import type { billDetailSchema } from '@super-mega/server/contracts/bill'
import { AttachmentUpload } from '@/features/files/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { OrganizationAccountSelect } from './organization-account-select.tsx'
import { fromCents, toCents } from './utils/money.ts'

type Bill = Pick<
  z.infer<typeof billDetailSchema>,
  | 'id'
  | 'billNo'
  | 'description'
  | 'direction'
  | 'organizationId'
  | 'organizationName'
  | 'customerId'
  | 'payeeId'
  | 'customerName'
  | 'totalAmount'
  | 'receivedAmount'
  | 'refundedAmount'
  | 'refundableAmount'
>
type Source = { id: number; transactionNo: string; occurredAt: Date; amount: string; available: string }
type Values = {
  sourceId: number
  amount: string
  organizationAccountId: number
  occurredAt: Dayjs
  channel: string
  name: string
  accountNo?: string
  reason: string
  attachmentIds: number[]
  status: 'pending' | 'confirmed'
}

export function RefundForm({
  bill,
  sources,
  onClose,
  onSaved
}: {
  bill: Bill
  sources: Source[]
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const access = useAccess()
  const [open, setOpen] = useState(true)
  const [saved, setSaved] = useState(false)
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<Values>()
  const [key] = useState(() => crypto.randomUUID())
  const sourceId = Form.useWatch<number>('sourceId', form)
  const amount = Form.useWatch<string | undefined>('amount', form)
  const status = Form.useWatch<Values['status']>('status', form)
  const source = sources.find((item) => item.id === sourceId)
  const outgoing = bill.direction === 'receivable'
  const action = outgoing ? '退款' : '付款退回'
  const save = useMutation(trpc.bill.recordMoney.mutationOptions())
  return (
    <ModalForm<Values>
      form={form}
      open={open}
      onOpenChange={setOpen}
      dateFormatter={false}
      title={outgoing ? '向客户退款' : '登记付款退回'}
      layout="vertical"
      width="42rem"
      scrollToFirstError
      modalProps={{
        onCancel: () => setOpen(false),
        afterClose: () => {
          void (async () => {
            try {
              if (saved) {
                await onSaved()
              }
            } catch {
              void message.error('退款已登记，刷新失败，请刷新页面')
            }
            onClose()
          })()
        },
        destroyOnHidden: true,
        mask: { closable: false },
        closable: !save.isPending
      }}
      initialValues={{
        sourceId: sources.length === 1 ? sources[0].id : undefined,
        occurredAt: dayjs(),
        channel: 'bankTransfer',
        name: bill.customerName,
        attachmentIds: [],
        status: access.can('bill.refund.confirm') ? 'confirmed' : 'pending'
      }}
      submitter={{
        searchConfig: { submitText: status === 'pending' ? '保存待确认退款' : `确认登记${action}` },
        submitButtonProps: { loading: save.isPending },
        resetButtonProps: { disabled: save.isPending }
      }}
      onFinish={async (values) => {
        const original = sources.find((item) => item.id === values.sourceId)
        if (!original || toCents(values.amount) <= BigInt(0) || toCents(values.amount) > toCents(original.available)) {
          form.setFields([{ name: 'amount', errors: ['退款金额须大于 0，且不能超过所选原款项的可退余额'] }])
          return false
        }
        try {
          await save.mutateAsync({
            organizationId: bill.organizationId,
            organizationAccountId: values.organizationAccountId,
            customerId: bill.customerId,
            payeeId: bill.payeeId,
            kind: outgoing ? 'receiptRefund' : 'paymentRefund',
            status: values.status,
            amount: values.amount,
            occurredAt: values.occurredAt.toDate(),
            channel: values.channel,
            counterpartySnapshot: { name: values.name.trim(), accountNo: values.accountNo?.trim() || undefined },
            note: values.reason.trim(),
            attachmentIds: values.attachmentIds,
            idempotencyKey: key,
            allocations: [{ billId: bill.id, amount: values.amount, refundOfAllocationId: original.id, note: null }]
          })
          setSaved(true)
          setOpen(false)
          void message.success(values.status === 'confirmed' ? `${action}已登记` : '退款已保存为待确认')
          return true
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '退款登记失败')
          return false
        }
      }}
    >
      <div className="border-border mb-4 border-b border-solid pb-3">
        <div className="text-sm font-medium">
          {outgoing
            ? `${bill.organizationName} → ${bill.customerName}`
            : `${bill.customerName} → ${bill.organizationName}`}
        </div>
        <p className="text-content-muted my-1">
          账单 {bill.billNo} · {bill.description}
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span>
            {outgoing ? '累计收款' : '累计付款'} ¥{formatAmount(bill.receivedAmount)}
          </span>
          <span>已退 ¥{formatAmount(bill.refundedAmount)}</span>
          <span>
            {outgoing ? '待退款' : '待收回'} ¥{formatAmount(bill.refundableAmount)}
          </span>
        </div>
      </div>
      <p className="text-content-muted mb-4 text-xs">
        退费或退押金：先调整账单金额，再登记实际退款。仅退回资金会减少净收款，可能重新产生待收款。
      </p>
      <Form.Item
        name="sourceId"
        label={outgoing ? '退回哪笔收款' : '对应原付款'}
        rules={[{ required: true, message: '请选择原款项' }]}
        extra={
          source
            ? `原款项 ¥${formatAmount(source.amount)} · 尚可退 ¥${formatAmount(source.available)}`
            : '每次关联一笔原款项，支持分次退回。'
        }
      >
        <Select
          placeholder="选择原款项"
          onChange={() => form.setFieldValue('amount', undefined)}
          options={sources.map((item) => ({
            value: item.id,
            label: `${item.transactionNo} · ${dayjs(item.occurredAt).format('YYYY-MM-DD')} · 可退 ¥${formatAmount(item.available)}`
          }))}
        />
      </Form.Item>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item
          name="amount"
          label="本次退款金额（元）"
          dependencies={['sourceId']}
          rules={[
            { required: true, message: '请填写本次退款金额' },
            {
              validator: (_, value: string | undefined) =>
                !value || (toCents(value) > BigInt(0) && source && toCents(value) <= toCents(source.available))
                  ? Promise.resolve()
                  : Promise.reject(new Error('退款金额须大于 0，且不超过原款项可退余额'))
            }
          ]}
        >
          <InputNumber stringMode precision={2} min="0.01" className="w-full!" placeholder="填写实际退回金额" />
        </Form.Item>
        <Form.Item name="occurredAt" label="退款时间" rules={[{ required: true }]}>
          <DatePicker showTime className="w-full!" />
        </Form.Item>
      </div>
      <RefundBalanceNotice bill={bill} amount={amount} maximum={source?.available} />
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item
          name="organizationAccountId"
          label={outgoing ? '退款支出账户' : '退款收回账户'}
          rules={[{ required: true }]}
        >
          <OrganizationAccountSelect purpose="bill.refund.record" organizationId={bill.organizationId} />
        </Form.Item>
        <Form.Item name="channel" label="退款方式" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'bankTransfer', label: '银行转账' },
              { value: 'wechat', label: '微信' },
              { value: 'alipay', label: '支付宝' },
              { value: 'cash', label: '现金' }
            ]}
          />
        </Form.Item>
        <Form.Item
          name="name"
          label={outgoing ? '收款人户名' : '退款方户名'}
          rules={[{ required: true, whitespace: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="accountNo" label={outgoing ? '收款人账号' : '退款方账号'}>
          <Input />
        </Form.Item>
      </div>
      <Form.Item
        name="reason"
        label="退款原因"
        rules={[{ required: true, whitespace: true, message: '请填写退款原因' }]}
      >
        <Input.TextArea rows={2} maxLength={5000} placeholder="例如：取消服务退费、退租差额、押金退回" />
      </Form.Item>
      <Form.Item name="attachmentIds" label="退款凭证">
        <AttachmentUpload purpose="moneyAttachment" maxCount={30} />
      </Form.Item>
      <Form.Item name="status" label="登记状态">
        <Select
          options={[
            { value: 'confirmed', disabled: !access.can('bill.refund.confirm'), label: '已实际退款，确认记录' },
            { value: 'pending', label: '待确认，暂不计入退款金额' }
          ]}
        />
      </Form.Item>
      <p className="text-content-muted mb-0 text-sm">仅记录实际退款，不发起银行或第三方转账。</p>
    </ModalForm>
  )
}

function RefundBalanceNotice({ bill, amount, maximum }: { bill: Bill; amount?: string; maximum?: string }) {
  const refund = toCents(amount ?? '0')
  if (!maximum || refund <= BigInt(0) || refund > toCents(maximum)) {
    return null
  }
  const outgoing = bill.direction === 'receivable'
  const net = toCents(bill.receivedAmount) - toCents(bill.refundedAmount) - refund
  const balance = toCents(bill.totalAmount ?? '0') - net
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" role="status" aria-live="polite">
      <Typography.Text type={balance > BigInt(0) ? 'warning' : 'secondary'}>
        {`确认后${outgoing ? '待收' : '待付'} ¥${fromCents(balance > BigInt(0) ? balance : BigInt(0))}，${outgoing ? '待退款' : '待收回'} ¥${fromCents(balance < BigInt(0) ? -balance : BigInt(0))}`}
      </Typography.Text>
      <Tooltip
        title={
          balance > BigInt(0)
            ? '此次退款会重新形成欠款。若属于退费或退押金，请先调整账单金额。'
            : '登记实际资金退回，不改变账单应收应付金额。'
        }
      >
        <span tabIndex={0} className="text-content-muted cursor-help underline decoration-dotted underline-offset-4">
          {balance > BigInt(0) ? '将重新形成欠款' : '金额说明'}
        </span>
      </Tooltip>
    </div>
  )
}
